import { useEffect, useState } from 'react';
import { Building2, Home } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Modal } from '@/components/Shared/Modal';
import { Button } from '@/components/Shared/Button';
import { FacilityFlowCoreModulesPlanningEntitiesWorkLocation as WorkLocationEnum } from '@/api/generated/models';
import type { WorkLocation } from '@/store/useTimeclockStore';
import type { WorkLocationDialogState } from '@/hooks/useWorkLocationQuestion';

const LOCATION_OPTIONS: { location: WorkLocation; key: string; icon: typeof Building2 }[] = [
  { location: WorkLocationEnum.NUMBER_1, key: 'office', icon: Building2 },
  { location: WorkLocationEnum.NUMBER_2, key: 'homeOffice', icon: Home },
];

interface WorkLocationDialogProps extends WorkLocationDialogState {
  busy?: boolean;
}

/**
 * Asks "Homeoffice oder Büro?" before clock-in, for employees the administration allows to work
 * from home. Driven by useWorkLocationQuestion via useClockStamp; the vehicle dialog follows
 * unless home office is chosen.
 */
export function WorkLocationDialog({
  isOpen,
  initial,
  busy = false,
  onConfirm,
  onCancel,
}: WorkLocationDialogProps) {
  const { t } = useTranslation('timeclock');
  const [location, setLocation] = useState<WorkLocation | null>(null);

  useEffect(() => {
    if (isOpen) setLocation(initial);
  }, [isOpen, initial]);

  const confirm = () => {
    if (location !== null) onConfirm(location);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={t('workLocationDialog.title')}
      size="sm"
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {t('workLocationDialog.cancel')}
          </Button>
          <Button onClick={confirm} disabled={busy || location === null}>
            {t('workLocationDialog.confirm')}
          </Button>
        </div>
      }
    >
      <fieldset>
        <legend className="text-[13px] font-medium text-slate-700 mb-2">{t('workLocation.label')}</legend>
        <div className="grid grid-cols-2 gap-2">
          {LOCATION_OPTIONS.map(({ location: option, key, icon: Icon }) => {
            const selected = location === option;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={selected}
                onClick={() => setLocation(option)}
                className={`flex min-h-[72px] flex-col items-center justify-center gap-1.5 rounded-lg border px-2 py-3 text-[13px] font-medium transition-colors ${
                  selected
                    ? 'border-[#00A871] bg-emerald-50 text-emerald-800'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={2} aria-hidden />
                <span className="text-center leading-tight">{t(`workLocation.${key}`)}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
    </Modal>
  );
}
